import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogIn, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PerlaLogo } from "@/components/site/PerlaLogo";

const formatErr = (detail) => {
  if (!detail) return "Errore imprevisto";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const landingFor = (u) => {
    if (u?.role === "operatore") return "/admin/home";
    if (u?.role === "setter") return "/admin/setting";
    return "/admin";
  };

  React.useEffect(() => {
    if (user) nav(landingFor(user), { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success("Benvenuto");
      nav(landingFor(u || user), { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-5" data-testid="admin-login-page">
      <div className="absolute top-6 left-6">
        <Link to="/" className="text-[#FAFAF7]/50 hover:text-[#FF5A1F] flex items-center gap-2 text-xs uppercase tracking-widest" data-testid="admin-login-back">
          <ArrowLeft size={14} /> Torna al sito
        </Link>
      </div>
      <div className="w-full max-w-md bg-[#1A1A1A] border border-[#2A2A2A] p-8">
        <div className="mb-8">
          <PerlaLogo light />
          <div className="mt-3 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">CMS Admin</div>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/50 font-bold">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-login-email"
              className="mt-2 block w-full bg-[#0A0A0A] border-2 border-[#3A3A3A] py-3 px-4 text-[#FAFAF7] focus:outline-none focus:border-[#FF5A1F]"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/50 font-bold">Password</span>
            <div className="relative mt-2">
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-login-password"
                className="block w-full bg-[#0A0A0A] border-2 border-[#3A3A3A] py-3 pl-4 pr-12 text-[#FAFAF7] focus:outline-none focus:border-[#FF5A1F]"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FAFAF7]/50 hover:text-[#FF5A1F]"
                aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                data-testid="admin-login-toggle-pwd"
                tabIndex={-1}
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          <button type="submit" disabled={busy} className="btn-cta w-full disabled:opacity-60" data-testid="admin-login-submit">
            <LogIn size={16} /> {busy ? "..." : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}
